"use client";

/**
 * Prefer-over DialKit dual-bound range (C23 / P13.A).
 * Vendor DialKit Slider is single-value — compose two DialKitSliders with lo≤hi clamp.
 * Zero Radix / bare repo Slider imports (Prefer-over only).
 */

import type { FocusEvent as ReactFocusEvent } from "react";
import { cn } from "../../../lib";
import { DialKitSlider } from "./dialkit-slider";
import "./dialkit-styles";

export interface DialKitRangeProps {
	label: string;
	value: [number, number];
	onChange: (value: [number, number]) => void;
	min: number;
	max: number;
	step?: number;
	disabled?: boolean;
	className?: string;
	/** When true, hides per-slider vendor labels (FieldType supplies chrome). */
	hideLabel?: boolean;
	onBlur?: (event: ReactFocusEvent<HTMLDivElement>) => void;
	"aria-invalid"?: boolean;
}

function clamp(n: number, lo: number, hi: number): number {
	return Math.min(hi, Math.max(lo, n));
}

/**
 * Prefer-over DialKit range — minimum + maximum DialKitSlider pair.
 */
export function DialKitRange({
	label,
	value,
	onChange,
	min,
	max,
	step = 1,
	disabled = false,
	className,
	hideLabel = false,
	onBlur,
	"aria-invalid": ariaInvalid,
}: DialKitRangeProps) {
	const lo = clamp(value[0], min, max);
	const hi = clamp(Math.max(value[1], lo), min, max);

	return (
		<div
			data-dialkit-range
			role="group"
			aria-label={label}
			aria-invalid={ariaInvalid || undefined}
			aria-disabled={disabled || undefined}
			className={cn(
				"flex min-w-0 flex-col gap-2",
				disabled && "pointer-events-none opacity-50",
				className,
			)}
			onBlur={onBlur}
		>
			<DialKitSlider
				label={hideLabel ? "\u00a0" : `${label} minimum`}
				hideLabel={hideLabel}
				value={lo}
				min={min}
				max={hi}
				step={step}
				disabled={disabled}
				onChange={(next) => {
					const nextLo = clamp(next, min, hi);
					onChange([nextLo, hi]);
				}}
			/>
			<DialKitSlider
				label={hideLabel ? "\u00a0" : `${label} maximum`}
				hideLabel={hideLabel}
				value={hi}
				min={lo}
				max={max}
				step={step}
				disabled={disabled}
				onChange={(next) => {
					const nextHi = clamp(next, lo, max);
					onChange([lo, nextHi]);
				}}
			/>
		</div>
	);
}
